"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function ProgrammeOverview() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [mouse, setMouse] = useState({
    x: 50,
    y: 50
  });
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const handleMove = (e: MouseEvent) => {
      const rect = node.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width * 100;
      const y = (e.clientY - rect.top) / rect.height * 100;
      setMouse({
        x,
        y
      });
    };
    node.addEventListener("mousemove", handleMove);
    return () => node.removeEventListener("mousemove", handleMove);
  }, []);
  useEffect(() => {
    const lines = 4;
    const interval = setInterval(() => {
      setRevealed(prev => {
        if (prev >= lines) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 220);
    return () => clearInterval(interval);
  }, []);
  const headlineLines = [{
    text: "A PATH",
    accent: false
  }, {
    text: "AS UNIQUE AS",
    accent: false
  }, {
    text: "YOUR",
    accent: false
  }, {
    text: "POTENTIAL.",
    accent: true
  }];
  return <section data-section-id="2759" id="programme-overview" ref={sectionRef} className="bg-background text-foreground relative flex min-h-[90vh] w-full items-center justify-center overflow-hidden px-4 py-20 sm:px-8 md:px-16">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 transition-[background-position] duration-700 ease-out" style={{
      background: `radial-gradient(600px circle at ${mouse.x}% ${mouse.y}%, hsla(0, 75%, 35%, 0.08), transparent 60%)`
    }} />

      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-[0.06]" style={{
      backgroundImage: "linear-gradient(var(--primary) 1px, transparent 1px), linear-gradient(90deg, var(--primary) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} />

      {}
      <div className="absolute left-4 top-1/2 z-10 hidden -translate-y-1/2 md:block">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          STRATEGY · GROWTH · EXCELLENCE
        </div>
      </div>

      {}
      <div className="absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 md:block">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground" style={{
        writingMode: "vertical-rl"
      }}>
          EST. MMXXIV · ELITE ACADEMY
        </div>
      </div>

      {}
      <div className="relative z-20 mx-auto flex w-full max-w-6xl flex-col items-center text-center">
        {}
        <div className="mb-10 flex items-center gap-4 sm:mb-14">
          <span className="h-px w-8 bg-primary/40" />
          <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-primary sm:text-xs">
            The Leadership Track / Cohort Alpha
          </span>
          <span className="h-px w-8 bg-primary/40" />
        </div>

        {}
        <h1 className="font-default font-bold uppercase leading-[0.95] tracking-tight text-foreground">
          {headlineLines.map((line, i) => <span key={i} data-index={i} className="block overflow-hidden">
              <span className={`inline-block text-[3rem] transition-all duration-[900ms] ease-out sm:text-[5rem] md:text-[7rem] lg:text-[10rem] ${revealed > i ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"} ${line.accent ? "text-primary" : ""}`} style={{
            transitionDelay: `${i * 80}ms`
          }}>
                {line.text}
              </span>
            </span>)}
        </h1>

        {}
        <p className={`mt-10 max-w-[600px] font-serif text-lg italic leading-relaxed text-foreground/80 transition-all duration-1000 sm:text-xl ${revealed >= 4 ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
        transitionDelay: "200ms"
      }}>
          The philosophy of our programme begins with a single question — what is your
          legacy? At the Academy, our mentors translate ambition, skill, and vision into
          a strategic roadmap that exists only for your success.
        </p>

        {}
        <div className={`mt-12 transition-all duration-1000 ${revealed >= 4 ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
        transitionDelay: "400ms"
      }}>
          <Button asChild size="lg" className="h-14 rounded-md bg-primary px-10 font-default text-sm font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-2xl hover:bg-primary/90" style={{
          boxShadow: "8px 8px 0px 0px hsl(0 75% 35%)"
        }}>
            <Link to="https://form.jotform.com/africabusinessschool12/course-application-form" newTab>
              Apply for Admission
            </Link>
          </Button>
        </div>
      </div>

      {}
      <div className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2">
        <div className="flex animate-pulse items-center gap-2 font-mono text-[10px] uppercase tracking-[0.4em] text-primary/60 sm:text-xs">
          <ArrowDown className="h-3 w-3" />
          <span>Explore the Curriculum</span>
        </div>
      </div>

      {}
      <div className="absolute left-4 top-6 z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground sm:left-8 sm:top-8">
        VOL. 001
      </div>
      <div className="absolute right-4 top-6 z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground sm:right-8 sm:top-8">
        ELITE ACADEMY
      </div>
    </section>;
}